
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton, Link,  Tooltip,
  Typography, useMediaQuery,
  useTheme,
} from '@mui/material'
import { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {  WaterTestStripSelector } from '../../../store/waterTestStrip'
import { WaterQualityIndexSelectors } from '../../../store/waterQualityIndex'
import { OutlinedButton } from '../../Button'
import { CloseIcon } from '../../icons/CloseIcon'
import { WaterStatusBox } from '../../WaterStatus'
import { AddressPinIcon } from '../../icons/AddressPinIcon'
import SearchIcon from '@mui/icons-material/Search'
import { AppActions, AppSelector } from '../../../store/app'
import { AppDispatch } from '../../../store'
import { SnackbarActions } from '../../../store/snackbar'
import { UploadRoundedIcon } from '../../icons'
import { UrlMapSelectors } from '../../../store/urlMaps/urlMap..selector'
import { UrlMapActions } from '../../../store/urlMaps/urlMap.slice'
import { useParams } from 'react-router-dom'

export const TestStripSummaryModal: FC = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const params = useParams()
  const waterTestStripResult = useSelector(WaterTestStripSelector.selectWaterTestStripResult)
  const groupedResults = useSelector(WaterTestStripSelector.makeWaterTestStripGroupedResults)
  const waterCoreModalOpen = useSelector(AppSelector.getwaterCoreSnapscoreModalState)
  const request = useSelector(WaterTestStripSelector.makeWaterTestKitRequestLatest)
  const urlMapData = useSelector(UrlMapSelectors.data)
  const signals = useSelector(WaterQualityIndexSelectors.selectSignals)
  const overall = useSelector(WaterQualityIndexSelectors.selectOverall)
  const { Good = 0, Caution = 0, High = 0, Low = 0, NA = 0 } = groupedResults || {}
  const handleClose = () => {
    dispatch(AppActions.toggleWaterCoreSnapscoreModal())
  }

  const fetchCurrentUrlMap = async () => {

    if(urlMapData){
      return urlMapData.shortUrl
    }
    const urlMap = await dispatch(UrlMapActions.GetUrlMapAsync(params.id!))
    if(UrlMapActions.GetUrlMapAsync.fulfilled.match(urlMap)){
      return  urlMap.payload.shortUrl
    }

    const newUrlMap = await dispatch(UrlMapActions.CreateUrlMapAsync({
      url: window.location.href,
      prefix:'/u',
      type: 'test_strip_results',
      id: params.id
    }, {}))
    if(UrlMapActions.CreateUrlMapAsync.fulfilled.match(newUrlMap)){
      return  newUrlMap.payload.shortUrl
    }
    return 'unknown'
  }

  const createShareString = async () => {
    const shortUrl = await fetchCurrentUrlMap()

    const prioritized = [
      ...signals.filter(s => s.severity === 'action'),
      ...signals.filter(s => s.severity === 'monitor'),
    ].slice(0, 3)

    const signalLines = prioritized.map(s => {
      const emoji = s.severity === 'action' ? '🚨' : '⚠️'
      const name = s.signalKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      return `${emoji} ${name}`
    })

    let str = ''
    str += "I just tested my home's water quality with SIPP 💧\n"
    str += `📍 ${request && request.address.city}, ${request && request.address.state}\n`
    str += "\n"

    if (prioritized.length > 0) {
      str += `Found ${prioritized.length} thing${prioritized.length > 1 ? 's' : ''} that need${prioritized.length === 1 ? 's' : ''} attention:\n`
      str += signalLines.join('\n') + '\n'
    } else {
      str += "✅ Great news — my water is looking good!\n"
    }

    str += "\n"
    if (overall) {
      str += `Overall water grade: ${overall.band}\n`
      str += "\n"
    }
    str += `See my full results: ${shortUrl}\n`
    str += "\n"
    str += "#WhatsInYourWater #SIPPSafely\n"

    return str
  }
  const handleShareResultsMobile = async () => {
    const copyStr = await createShareString()
    if(navigator && navigator.share) {
      try {
        await navigator.share({
         // title: 'Sipplytics Water Analysis Result',
          text: copyStr,
         // url: window.location.href
        })
      } catch(error:any){

        dispatch(SnackbarActions.showSnackbar({message: error.message}))
      }
    } else {
      dispatch(SnackbarActions.showSnackbar({message: 'Navigator not available on this medium'}))
    }

  }
  const handleCopyToClipBoard = async () => {
    const copyStr = await createShareString()
    navigator.clipboard.writeText(copyStr)
    dispatch(
      SnackbarActions.showSnackbar({
        message: 'Copied to Clipboard!',
        options: { variant: 'success' },
      }),
    )
  }

  return (
    <Dialog open={waterCoreModalOpen} maxWidth="xs" slotProps={{
      paper: {
        sx: {borderRadius: 6}
      }
    }}>
      <DialogTitle>Water Analysis Results</DialogTitle>
      <IconButton
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <Divider />
      <DialogContent>
        <Grid
          borderRadius={4}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'nowrap',
            px: 1.5,
            py: 1,
            background: theme.palette.grey[100],
          }}
        >
          <Typography>Sipplytics Water Analysis Result</Typography>

          <Typography>💧 16 attribute tap water test</Typography>
          <Box component='div'>
            <AddressPinIcon fill={theme.palette.primary.main} sx={{ fontSize: 20, mt: 0.75, mb:'-4px', mr:'4px' }} />
            <Typography sx={{display:'inline-block'}}>{request && request.address.city}, {request && request.address.state}</Typography>
          </Box>
          <Divider orientation='horizontal' flexItem sx={{my:'4px'}} />
          {waterTestStripResult && !isMobile &&
            Object.entries(waterTestStripResult).map(([category, parameters]) => {
              return (
                <Box
                  key={category}
                  sx={{
                    flexBasis: '100%',
                    py: 1,
                    px: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                  }}
                >
                  <Typography>{category}:</Typography>
                  <Box sx={{display:'flex', gap:1}}>
                    {parameters.map(({ label = '', name, value }) => {

                      return (
                        <Tooltip title={`${name}: ${value}`} key={`${category}_${name}`}>
                          <WaterStatusBox size="small" status={label as any}  />
                        </Tooltip>
                      )
                    })}
                  </Box>

                </Box>
              )
            })}
          <Grid container flexDirection={{xs:'column', md:'row'}} gap={{xs:2, md:0.5}} justifyContent={{xs:'left', md:'center'}} sx={{my:1}}>
            <Grid container flexDirection='row' gap={1} flexWrap='nowrap' alignItems='center' size={{xs:12, md:'auto'}} >
              <WaterStatusBox size="xsmall" status="Good"  />
              <Typography variant='subtitle2' sx={{whiteSpace:'nowrap'}}>Safe: {Good}</Typography>

            </Grid>
            {!isMobile && <Divider orientation='vertical' flexItem />}
            <Grid container flexDirection='row' gap={1} flexWrap='nowrap' alignItems='center' size={{xs:12, md:'auto'}}>
              <WaterStatusBox size="xsmall" status="Caution"  />
              <Typography variant='subtitle2' sx={{whiteSpace:'nowrap'}}>Caution: {Caution}</Typography>

            </Grid>
            {!isMobile && <Divider orientation='vertical' flexItem />}
            <Grid container flexDirection='row' gap={1} flexWrap='nowrap' alignItems='center' size={{xs:12, md:'auto'}}>
              <WaterStatusBox size="xsmall" status="High"  />
              <Typography variant='subtitle2' sx={{whiteSpace:'nowrap'}}>Unsafe: {High + Low}</Typography>

            </Grid>
            {!isMobile && <Divider orientation='vertical' flexItem />}
            <Grid container flexDirection='row' gap={1} flexWrap='nowrap' alignItems='center' size={{xs:12, md:'auto'}}>
              <WaterStatusBox size="xsmall" />
              <Typography variant='subtitle2' sx={{whiteSpace:'nowrap'}}>Not Rated: {NA}</Typography>

            </Grid>
          </Grid>
          <Divider orientation='horizontal' flexItem sx={{my:'4px'}} />
          <Typography>What's in your water?</Typography>
          <Box sx={{display:'flex', flexDirection:'row'}}>
            <SearchIcon/>
            <Typography>Find out now at <Link  href="https://sipplytics.com">sipplytics.com</Link></Typography>
          </Box>
          <Typography sx={{mt:2}}>#WhatsInYourWater #SIPPSafely</Typography>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', mb: 1 }}>
        <OutlinedButton
          onClick={()=>handleCopyToClipBoard()}
          startIcon={<ContentCopyIcon />}
        >
          Copy Result
        </OutlinedButton>
        {navigator &&
        <OutlinedButton

          onClick={()=>handleShareResultsMobile()}
          startIcon={<UploadRoundedIcon stroke={theme.palette.primary.main} />}
        >
          Share Results
        </OutlinedButton>}
      </DialogActions>
    </Dialog>
  )
}
